import { getInitials } from '@tempo-io/string-util';
import { Avatar, AvatarFallback, AvatarImage } from '@tempo-io/sui-avatar';
import { Select } from '@tempo-io/sui-select';
import { useTranslation } from 'react-i18next';
import { useOrgMemberByIdQueryWithPublished } from '@/components/modular-item-panel/hooks/useOrgMemberByIdQueryWithPublished';
import { UserDisplayButton } from './UserDisplayButton';

interface UserFieldDisplayProps {
  userId?: string;
  isEditable: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  placeholder?: string;
}

interface UserOption {
  value: string;
  label: string;
  pictureUrl?: string;
  avatarInitials: string;
}

const formatUserOptionLabel = (option: UserOption) => {
  return (
    <div className="flex items-center gap-2">
      <Avatar size="sm">
        <AvatarImage src={option.pictureUrl} alt={option.label} />
        <AvatarFallback className="text-xs">{option.avatarInitials}</AvatarFallback>
      </Avatar>
      <span>{option.label}</span>
    </div>
  );
};

export const UserFieldDisplay = ({
  userId,
  isEditable,
  onClick,
  onRemove,
  placeholder,
}: UserFieldDisplayProps) => {
  const { t } = useTranslation();
  const { data: user } = useOrgMemberByIdQueryWithPublished(userId || '');

  const userOption: UserOption | null = user
    ? {
        value: userId || '',
        label: user.displayLabel,
        pictureUrl: user.pictureUrl ?? undefined,
        avatarInitials: getInitials(user.displayLabel) || '',
      }
    : null;

  if (!isEditable) {
    return (
      <Select
        value={userOption}
        options={userOption ? [userOption] : []}
        formatOptionLabel={formatUserOptionLabel}
        placeholder={placeholder || t(($) => $.adaptivePlanner.itemPanel.userField.noUserSelected)}
        isDisabled
      />
    );
  }

  return (
    <UserDisplayButton
      userId={userId}
      onClick={onClick}
      onRemove={onRemove}
      placeholder={placeholder}
    />
  );
};
